import { useCallback, useContext, useEffect } from 'react';
import useCustomHook from '@/hooks/useCustomHook';
import { GameLoopContext } from '@/shared/GameComponents/GameLoopContext/GameLoopContext';
import { getRandomItem } from '@/modules/gameUtils/random';
import useWait from '@/shared/GameComponents/useWait.hook';

export default function useRaceConceptAnimation({ concept }) {
  const game = useContext(GameLoopContext);
  const { wait, cancelWait } = useWait();
  const { state, actions } = useCustomHook({
    defaultState: {
      animId: 'normal',
      transform: {
        x: 0,
        y: 0,
        width: 25,
        height: 25,
        pivot: { x: 50, y: 100 },
      },
      bgTransform: {
        x: 0,
        y: 0,
        width: 25,
        height: 25,
        pivot: { x: 50, y: 100 },
      },
      heading: null,
      speeds: {
        normal: game.state.gameConf.mode == 'writing' ? 100 / 15 : 100 / 7,
        playingOptionAudios: 100 / 15,
        success: 100 / 4,
      },
      velocity: { x: 0, y: 0 },
      bgVelocity: { x: 0, y: 0 },
      acceleration: { x: 0, y: 0 },
      showBadFeedback: false,
      userAnswer: null,
      currentConcept: null,
    },
    actions: {
      onAnimationLastFrame: (animId) => {
        if (animId == 'score') {
          actions.setState({
            animId: 'normal',
          });
        }
      },
    },
  });

  // Initialize concept animation state
  useEffect(() => {
    const heading = concept.onlyTowards ?? getRandomItem(['LEFT', 'RIGHT']);
    actions.setState({
      currentConcept: concept,
      transform: calculateTransform(heading),
      bgTransform: calculateBgTransform(heading),
      velocity: getInitialVelocity(state.speeds['paused'], heading),
      bgVelocity: getInitialBgVelocity(state.speeds['normal'] / 4, heading),
      acceleration: { x: 0, y: 0 },
      heading,
    });
    game.actions.setState({ timedOut: false, givingFeedback: false });
  }, [concept]);

  // Make the movement slower while playing concept option audios
  useEffect(() => {
    if (state.heading) {
      const speed = game.state.playingOptionAudios
        ? state.speeds['playingOptionAudios']
        : state.speeds['normal'];
      actions.setState({
        velocity: getInitialVelocity(speed, state.heading),
      });
    }
  }, [game.state.playingOptionAudios, state.heading, concept]);

  // Update animation position on each frame
  useEffect(() => {
    if (concept.id == state.currentConcept?.id && state.velocity) {
      const {
        transform: { x: lastX },
        bgTransform: { x: lastBgX },
        velocity: { x: lastVx },
        bgVelocity: { x: lastBgVx },
        acceleration: { x: ax },
        heading,
      } = state;

      const vx = lastVx + (ax * game.state.deltaTime) / 1000;
      const x = lastX + (vx * game.state.deltaTime) / 1000;
      const bgVx = lastBgVx;
      const bgX = lastBgX + (bgVx * game.state.deltaTime) / 1000;

      if (
        !game.state.givingFeedback &&
        !game.state.timedOut &&
        ((x > 100 && heading == 'RIGHT') || (x < 0 && heading == 'LEFT'))
      ) {
        game.actions.setState({ timedOut: true });
      } else {
        actions.setState({
          transform: { ...state.transform, x },
          bgTransform: { ...state.bgTransform, x: bgX },
          velocity: { x: vx, y: 0 },
          bgVelocity: { x: bgVx, y: 0 },
        });
      }
    }
  }, [game.state.frame]);

  //Play success animation on right answer
  useEffect(() => {
    if (game.state.currentAnswerId == concept.id) {
      scoreDecelerateAndAccelerate();
    }

    async function scoreDecelerateAndAccelerate() {
      actions.setState({
        animId: 'score',
        acceleration: { x: state.heading == 'RIGHT' ? -50 : 50, y: 0 },
      });
      await wait(800);
      actions.setState({
        acceleration: { x: state.heading == 'RIGHT' ? 200 : -200, y: 0 },
      });
    }
    return cancelWait;
  }, [game.state.currentAnswerId, concept.id]);

  const getInitialVelocity = useCallback(
    (speed, heading) => {
      return speed
        ? {
            x: ({ RIGHT: 1, LEFT: -1 }[heading] ?? 0) * speed,
            y: 0,
          }
        : { x: 0, y: 0 };
    },
    [concept]
  );

  const getInitialBgVelocity = useCallback(
    (speed, heading) => {
      const bgVelocity = speed
        ? {
            x: ({ LEFT: 1, RIGHT: -1 }[heading] ?? 0) * speed,
            y: 0,
          }
        : { x: 0, y: 0 };
      return bgVelocity;
    },
    [concept]
  );

  const calculateTransform = useCallback(
    (heading) => {
      const flipped = heading != concept.orientation;
      const initialPosition = {
        x:
          {
            RIGHT: 0 - Math.abs(state.transform.width) / 2,
            LEFT: 100 + Math.abs(state.transform.width) / 2,
          }[heading] ?? 0,
        y: 40,
      };

      return {
        ...state.transform,
        ...initialPosition,
        width: flipped
          ? -Math.abs(state.transform.width)
          : Math.abs(state.transform.width),
      };
    },
    [concept]
  );

  const calculateBgTransform = useCallback(
    (heading) => {
      const initialPosition = {
        x:
          {
            LEFT: 0,
            RIGHT: 90,
          }[heading] ?? 0,
        y: 40,
      };
      const flipped = heading != concept.orientation;
      return {
        ...state.bgTransform,
        ...initialPosition,
        width: flipped
          ? Math.abs(state.bgTransform.width)
          : -Math.abs(state.bgTransform.width),
      };
    },
    [concept]
  );

  return { state, actions };
}
